import { useEffect, useMemo } from 'react';

import { fetchMigrationStatusIfNeeded } from '@/data/migration/actions/fetchRipplingMigration';
import { CUTOVER, LOADING_DATA, type TagConfig } from '@/data/migration/model';
import {
  getIsMigrationReady,
  getMigrationLoaded,
  getMigrationStatus,
} from '@/data/migration/selectors/migrationStatus';
import { useWiwDispatch, useWiwSelector } from '@/store';

import moment from 'moment-timezone';

const DEFAULT_MIGRATION_WINDOW_DAYS = 30;

export default function useRipplingMigration() {
  const dispatch = useWiwDispatch();
  const migrationLoaded = useWiwSelector(getMigrationLoaded);
  const migrationActive = useWiwSelector(getIsMigrationReady);
  const migrationStatus = useWiwSelector(getMigrationStatus);

  const tagConfig = useMemo((): TagConfig | null => {
    return (migrationStatus?.get('tag_config') as TagConfig | null) ?? null;
  }, [migrationStatus]);

  const migrationWindowDays = tagConfig?.migration_window_days ?? DEFAULT_MIGRATION_WINDOW_DAYS;

  const migrationWindowEnd = useMemo(() => {
    return migrationStatus?.date('migration_completed_at');
  }, [migrationStatus]);

  const cutoverDate = useMemo(() => {
    return migrationStatus?.date('cutover_time');
  }, [migrationStatus]);

  const missedMigrationDate = useMemo(() => {
    return migrationActive && !cutoverDate && migrationWindowEnd?.isBefore(moment());
  }, [migrationActive, cutoverDate, migrationWindowEnd]);

  const cutoverComplete = migrationStatus?.status === CUTOVER;

  useEffect(() => {
    dispatch(fetchMigrationStatusIfNeeded());
  }, []);

  return {
    migrationLoaded,
    migrationActive,
    migrationStep: migrationStatus?.status ?? LOADING_DATA,
    migrationWindowEnd,
    migrationWindowDays,
    cutoverDate,
    missedMigrationDate,
    cutoverComplete,
    tagConfig,
  };
}
